import type { CSSProperties } from 'react';
import { alpha, type SxProps, type Theme } from '@mui/material';
import { FlowTextAlign, type FormItemUiDefinition } from '../types/formItem.ts';
import { darkerColor } from './colors.ts';

export type UIDefinitionTextStyles = {
  fontWeight?: CSSProperties['fontWeight'];
  fontStyle?: CSSProperties['fontStyle'];
  textDecoration?: CSSProperties['textDecoration'];
  color?: CSSProperties['color'];
};

export const getUiDefinitionTextStyles = (formItemUiDefinition?: FormItemUiDefinition | null): UIDefinitionTextStyles => {
  const styles: CSSProperties = {};
  if (formItemUiDefinition?.textStyle?.bold) {
    styles.fontWeight = 'bold';
  }
  if (formItemUiDefinition?.textStyle?.italic) {
    styles.fontStyle = 'italic';
  }

  if (formItemUiDefinition?.textStyle?.underline) {
    styles.textDecoration = 'underline';
  }

  if (formItemUiDefinition?.textStyle?.color) {
    styles.color = formItemUiDefinition.textStyle.color;
  }

  return styles;
};

export type UIDefinitionContainerStyles = {
  backgroundColor?: CSSProperties['backgroundColor'];
  borderColor?: CSSProperties['borderColor'];
};

export const getUiDefinitionContainerStyles = (formItemUiDefinition?: FormItemUiDefinition | null): UIDefinitionContainerStyles => {
  const styles: CSSProperties = {};

  if (formItemUiDefinition?.attributeColor) {
    styles.backgroundColor = `${formItemUiDefinition.attributeColor} !important`;
  }

  if (formItemUiDefinition?.columnBorderColor) {
    styles.border = `1px solid ${formItemUiDefinition.columnBorderColor} !important`;
  }

  return styles;
};

export type UIDefinitionIconStyles = {
  fill?: CSSProperties['fill'];
};

export const getUIDefinitionIconStyles = (formItemUiDefinition?: FormItemUiDefinition | null): UIDefinitionIconStyles => {
  const styles: CSSProperties = {};

  if (formItemUiDefinition?.iconColor) {
    styles.fill = formItemUiDefinition.iconColor;
  }

  return styles;
};

export type UIDefinitionStyles = UIDefinitionTextStyles & UIDefinitionContainerStyles;

export const getUiDefinitionStyles = (formItemUiDefinition?: FormItemUiDefinition): UIDefinitionStyles => {
  return { ...getUiDefinitionTextStyles(formItemUiDefinition), ...getUiDefinitionContainerStyles(formItemUiDefinition) };
};

export const mapTextAlignToCss = (align?: string | number | null): 'left' | 'center' | 'right' => {
  if (typeof align === 'number') {
    if (align === 1) return 'center';
    if (align === 2) return 'right';
    return 'left';
  }
  switch (align) {
    case 'Center':
    case 'center':
      return 'center';
    case 'Right':
    case 'right':
      return 'right';
    default:
      return 'left';
  }
};

export const mapTextAlignToJustifyContent = (align?: string | number | null): 'flex-start' | 'center' | 'flex-end' => {
  if (typeof align === 'number') {
    if (align === 1) return 'center';
    if (align === 2) return 'flex-end';
    return 'flex-start';
  }
  switch (align) {
    case 'Center':
    case 'center':
      return 'center';
    case 'Right':
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

export const FLOW_TEXT_ALIGN_OPTIONS = [
  { value: FlowTextAlign.Left, labelKey: 'left' },
  { value: FlowTextAlign.Center, labelKey: 'center' },
  { value: FlowTextAlign.Right, labelKey: 'right' },
] as const;

export const DEFAULT_FLOW_TEXT_ALIGN = FlowTextAlign.Left;

/**
 * Compact outlined button styling used across detail/edit screens, calendar headers,
 * and filter buttons. Provides a consistent 32px height, 13px font, grey border,
 * and primary-color hover state.
 */
export const compactButtonSx: SxProps = {
  borderRadius: '8px',
  borderColor: 'grey.300',
  color: 'text.secondary',
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 500,
  padding: '4px 12px',
  minHeight: '32px',
  '&:hover': {
    borderColor: 'primary.main',
    color: 'primary.main',
    backgroundColor: 'transparent',
  },
};

export const getTouchPressButtonSx = (attributeColor?: string | null): SxProps<Theme> => {
  return (theme) => ({
    '@media (hover: none)': {
      '&:active': {
        backgroundColor:
          attributeColor && attributeColor.startsWith('#')
            ? darkerColor(attributeColor as `#${string}`, 10)
            : alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
      },
    },
  });
};
